import styled from '@emotion/styled';
import {Spacer, spacing} from '@nfq/react-grid';

import {Button} from 'UI/components/action/Button';

import {useSliderControls} from './useSliderControls';
import {Arrow} from 'Images/icons';

import type {EmblaCarouselType} from 'embla-carousel';

/**
 * The `ComponentProps` interface defines the properties for the `SliderControls` component.
 * It specifies the required configuration for integrating with an Embla carousel instance and providing test identification.
 * This interface ensures type safety when passing props to control carousel navigation and slide indicators.
 */
interface ComponentProps {
    /**
     * The Embla carousel API instance used to control slide navigation and track carousel state.
     * This optional property provides access to the carousel's methods for programmatic control of slide transitions.
     * When provided, it enables the slider controls to interact with the carousel for navigation functionality.
     */
    empblaApi?: EmblaCarouselType;
    /**
     * The `testId` property represents a unique identifier for testing purposes.
     * This property is crucial for uniquely identifying components during testing scenarios.
     * It allows test automation tools to reliably locate and interact with the slider controls component.
     *
     * @default 'SliderControls'
     */
    testId?: string;
}

/**
 * The `SliderControls` component provides navigation controls for an Embla carousel slider.
 * It renders previous/next buttons and dot indicators that allow users to navigate through slides.
 * The component integrates with the Embla carousel API to manage slide transitions and track the current slide position.
 *
 * @param props           The component props.
 * @param props.empblaApi The Embla carousel API instance used to control slide navigation and track carousel state.
 * @param props.testId    A unique identifier, usually in the form of a string, assigned to the component for testing purposes.
 * @returns A React element representing the `SliderControls` component with navigation buttons and dot indicators.
 *
 * @example
 * ```tsx
 * const MySliderControls = (
 *   <SliderControls
 *     empblaApi={emblaApiInstance}
 *     testId="carousel-controls"
 *   />
 * );
 * ```
 */
const SliderControls = ({empblaApi, testId = 'SliderControls'}: ComponentProps) => {
    const {
        nextBtnDisabled,
        onDotButtonClick,
        onNextButtonClick,
        onPrevButtonClick,
        prevBtnDisabled,
        scrollSnaps,
        selectedIndex
    } = useSliderControls(empblaApi);

    return (
        <Wrapper data-cy={testId}>
            <PrevButton
                aria-label="Previous Slide"
                as="button"
                icon={Arrow}
                isDisabled={prevBtnDisabled}
                variant="icon-only"
                onClick={onPrevButtonClick}
            />
            <Spacer maxX={11} x={6} />
            <Dots>
                {scrollSnaps.map((_, index) => (
                    <DotButton
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        aria-label={`Zu Slide ${index + 1} gehen`}
                        className={index === selectedIndex ? 'active' : ''}
                        onClick={() => onDotButtonClick(index)}
                    />
                ))}
            </Dots>
            <Spacer maxX={11} x={6} />
            <NextButton
                aria-label="Next Slide"
                as="button"
                icon={Arrow}
                isDisabled={nextBtnDisabled}
                variant="icon-only"
                onClick={onNextButtonClick}
            />
        </Wrapper>
    );
};

SliderControls.displayName = 'SliderControls';

export {SliderControls};

const Wrapper = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    margin: auto;
    width: 100%;
`;

const PrevButton = styled(Button)`
    flex: 0 0 4.8rem;
    rotate: 180deg;
`;

const Dots = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(5)};
    justify-content: center;
`;

const NextButton = styled(Button)`
    flex: 0 0 4.8rem;
`;

const DotButton = styled.button`
    background-color: ${({theme}) => theme.colors.textPrimary};
    border: none;
    border-radius: 50%;
    cursor: pointer;
    height: 0.9rem;
    opacity: 0.5;
    outline: none;
    padding: 0;
    transition: opacity 0.3s ease-in-out, background-color 0.3s ease-in-out;
    width: 0.9rem;

    &:hover {
        opacity: 1;
    }

    &:active, &.active {
        opacity: 1;
    }

    &:focus-visible {
        opacity: 1;
        outline: 2px solid ${({theme}) => theme.colors.tertiary};
        outline-offset: 2px;
    }
`;